import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip
} from '@mui/material';
import axios from 'axios';

const LifecycleHistorySection = ({ componentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!componentId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`/api/components/${componentId}/lifecycle`)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load lifecycle');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [componentId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!data) return null;

  const { fittings = [], services = [], inspections = [] } = data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        Installation History (Fittings)
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Aircraft</TableCell>
              <TableCell>Assembly</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Installed</TableCell>
              <TableCell>Removed</TableCell>
              <TableCell>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fittings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No installation history</TableCell>
              </TableRow>
            ) : (
              fittings.map((f, idx) => (
                <TableRow key={idx}>
                  <TableCell>{f.aircraftRegistration || '-'}</TableCell>
                  <TableCell>{f.assemblyTypeName || '-'}</TableCell>
                  <TableCell>{f.code || '-'}</TableCell>
                  <TableCell>{f.installedAt ? new Date(f.installedAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{f.removedAt ? new Date(f.removedAt).toLocaleDateString() : 'Current'}</TableCell>
                  <TableCell>{f.remarks || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold">
        Services (Overhaul, Repair)
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Completed By</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>No service records</TableCell>
              </TableRow>
            ) : (
              services.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.type || '-'}</TableCell>
                  <TableCell>{s.description || '-'}</TableCell>
                  <TableCell>{s.date ? new Date(s.date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>{s.completedBy || '-'}</TableCell>
                  <TableCell><Chip label={s.status} size="small" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold">
        Inspections
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Inspector</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inspections.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No inspection records</TableCell>
              </TableRow>
            ) : (
              inspections.map((i) => (
                <TableRow key={i.id}>
                  <TableCell>{i.type || '-'}</TableCell>
                  <TableCell>{i.description || '-'}</TableCell>
                  <TableCell>{i.date ? new Date(i.date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><Chip label={i.result} size="small" color={i.result === 'PASS' ? 'success' : i.result === 'FAIL' ? 'error' : 'default'} /></TableCell>
                  <TableCell>{i.inspectorName || i.completedBy || '-'}</TableCell>
                  <TableCell><Chip label={i.status} size="small" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LifecycleHistorySection;
